import React, { Fragment, useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  Grid,
  Paper,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  CircularProgress,
  Backdrop
} from "@mui/material";
import { ArrowBack as ArrowBackIcon, CloudDownload as CloudDownloadIcon } from "@mui/icons-material";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import { BASE_URL } from "../../../variables/api";
import useCheckSuperAdmin from "../../../hooks/useCheckSuperAdmin";

const ViewResponse = (props) => {
  const checkIfAdmin = useCheckSuperAdmin();
  const navigate = useNavigate();
  const { id } = useParams();

  let emaildomain = localStorage.getItem("emaildomain");
  if (checkIfAdmin) {
    let selected_school = localStorage.getItem("selected_school");
    try {
      emaildomain = JSON.parse(selected_school)?.value;
    } catch (e) {
      console.error("Error parsing selected_school", e);
    }
  }

  const [formData, setFormData] = useState({
    name: "",
    FormArr: [],
    description: "",
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .post(`${BASE_URL}/surveys/getresponse.php`, { id: id })
      .then((res) => {
        if (res.data && res.data.data) {
          setFormData((prevstate) => ({
            ...prevstate,
            name: res.data.data.survey?.name,
            FormArr: res.data.data.response?.responses || [],
            description: res.data.data.survey?.description,
          }));
        }
      })
      .catch(err => console.error(err))
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  const handleClickDetailedView = (questionId, questionName) => {
    // Clean up question name for URL if needed, or just pass ID
    // The original code passed questionName.
    navigate(`/details-question/${id}/${questionId}/${questionName}`);
  };

  return (
    <Box sx={{ py: { xs: 1, sm: 2 } }}>
      {/* Loading Overlay */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" />
          <Typography variant="h6">Loading Response...</Typography>
        </Box>
      </Backdrop>

      <Container maxWidth="xl">
        <Button
          component={Link}
          to="/" // Or back to MyForms list? The original had no back button in the code I read, but ViewForm did.
          // Wait, MyForms had a back button. ViewResponse didn't have one in the snippet I read?
          // Actually, I should probably link back to the responses list if possible.
          // But I don't know the survey ID easily here without storing it.
          // I'll just link to Home for now or go back in history.
          onClick={() => navigate(-1)}
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 3 }}
          variant="outlined"
        >
          Back
        </Button>

        <Box sx={{ textAlign: "center", mb: 4 }}>
          {emaildomain && (
            <img
              src={`/assets/${emaildomain}/logo.png`}
              alt="logo"
              style={{ maxWidth: "200px", height: "auto" }}
              onError={(e) => {e.target.style.display='none'}}
            />
          )}
        </Box>

        <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 2 }}>
          <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
            {formData.name}
          </Typography>
          <Box
            sx={{ mt: 2, typography: 'body1' }}
            dangerouslySetInnerHTML={{ __html: formData?.description }}
          />
        </Paper>

        <Grid container spacing={4}>
          <Grid item xs={12} md={9}>
            <Stack spacing={3}>
              {formData.FormArr.map((question, index) => (
                <Card key={index} sx={{ borderRadius: 2, boxShadow: 1 }}>
                  <CardContent>
                    <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                      Question {index + 1}
                    </Typography>
                    <Typography variant="h6" gutterBottom>
                      {question.Question}
                    </Typography>
                    
                    <Box sx={{ mt: 2 }}>
                      {/* Text/Email/Number/Date/Paragraph */}
                      {(question.type.value === "text" || question.type.value === "email" || question.type.value === "number" || question.type.value === "date" || question.type.value === "paragraph") && (
                        <TextField
                          fullWidth
                          multiline={question.type.value === "paragraph"}
                          rows={question.type.value === "paragraph" ? 4 : 1}
                          value={question.response || ""}
                          InputProps={{
                            readOnly: true,
                          }}
                          variant="filled"
                        />
                      )}

                      {/* Select */}
                      {question.type.value === "select" && (
                        <TextField
                          fullWidth
                          value={question.response?.value || question.response || ""}
                          InputProps={{
                            readOnly: true,
                          }}
                          variant="filled"
                          label="Selected Option"
                        />
                      )}

                      {/* Checkbox */}
                      {question.type.value === "checkbox" && (
                        <Stack>
                          {question.options.map((opt, optIndex) => (
                            <FormControlLabel
                              key={optIndex}
                              control={
                                <Checkbox
                                  checked={opt.response === true}
                                  disabled
                                />
                              }
                              label={opt.value}
                            />
                          ))}
                        </Stack>
                      )}

                      {/* Radio / Linescale */}
                      {(question.type.value === "radio" || question.type.value === "linescale") && (
                        <RadioGroup
                          row={question.type.value === "linescale"}
                          value={question.options.findIndex(o => o.response === true)}
                        >
                          {question.options.map((opt, optIndex) => (
                            <FormControlLabel
                              key={optIndex}
                              value={optIndex}
                              control={<Radio disabled checked={opt.response === true} />}
                              label={opt.value}
                              labelPlacement={question.type.value === "linescale" ? "bottom" : "end"}
                              sx={question.type.value === "linescale" ? { mx: 1 } : {}}
                            />
                          ))}
                        </RadioGroup>
                      )}

                      {/* File */}
                      {question.type.value === "file" && (
                        <Box>
                          {question.response && question.response.length > 0 ? (
                            (() => {
                              try {
                                const fileData = typeof question.response === 'string' ? JSON.parse(question.response) : question.response;
                                const driveId = fileData.driveid;
                                const fileName = fileData.fileName;
                                const downloadUrl = driveId && driveId.includes("https://") ? driveId : `https://drive.google.com/uc?id=${driveId}&export=download`;
                                
                                return (
                                  <Button
                                    variant="outlined"
                                    startIcon={<CloudDownloadIcon />}
                                    href={downloadUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {fileName || "Download File"}
                                  </Button>
                                );
                              } catch (e) {
                                return <Typography color="error">Error parsing file data</Typography>;
                              }
                            })()
                          ) : (
                            <Typography color="textSecondary">No file uploaded</Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12} md={3}>
            <Paper sx={{ position: 'sticky', top: 20, p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Questions Navigation
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List dense sx={{ maxHeight: '70vh', overflow: 'auto' }}>
                {formData.FormArr.map((question, index) => (
                  <ListItem key={index} disablePadding>
                    <ListItemButton onClick={() => handleClickDetailedView(question.id, question.Question)}>
                      <ListItemText 
                        primary={`Question ${index + 1}`} 
                        secondary={question.Question.length > 30 ? question.Question.substring(0, 30) + "..." : question.Question}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ViewResponse;
